import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const HEALTH_COLOR = "#8FB9B2";
const inputCls =
  "w-full px-2.5 py-1.5 rounded-md border border-input bg-white/70 text-sm focus:outline-none focus:border-foreground/40 transition-colors duration-200";

const emptyRow = () => ({ key: "", label: "", unit: "" });

function AddDeviceForm({ types, manufacturers, onAdded }) {
  const [name, setName] = useState("");
  const [typeId, setTypeId] = useState("");
  const [manufacturer, setManufacturer] = useState("generic");
  const [rows, setRows] = useState([emptyRow()]);
  const [busy, setBusy] = useState(false);

  const updateRow = (i, field, val) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const removeRow = (i) => setRows((rs) => (rs.length === 1 ? rs : rs.filter((_, idx) => idx !== i)));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Device name is required");
    if (!typeId) return toast.error("Please pick a device type");
    const measurements = rows
      .map((r) => ({ key: r.key.trim(), label: r.label.trim(), unit: r.unit.trim() }))
      .filter((r) => r.key && r.label);
    if (measurements.length === 0) return toast.error("Add at least one measurement (key + label)");

    setBusy(true);
    try {
      await api.post("/health/devices", {
        name: name.trim(),
        device_type_id: typeId,
        manufacturer,
        measurements,
      });
      toast.success("Device added");
      setName("");
      setTypeId("");
      setManufacturer("generic");
      setRows([emptyRow()]);
      onAdded();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-xl border border-border bg-card p-6 space-y-5" data-testid="add-device-form">
      <h2 className="font-serif text-xl text-foreground">Add a device</h2>

      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Device name</label>
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. My Garmin Watch" data-testid="device-name-input" />
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Device type</label>
          <select className={inputCls} value={typeId} onChange={(e) => setTypeId(e.target.value)} data-testid="device-type-select">
            <option value="">Select type…</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-muted-foreground mb-1">Manufacturer</label>
          <select className={inputCls} value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} data-testid="device-manufacturer-select">
            {manufacturers.map((m) => (
              <option key={m.key} value={m.key}>{m.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs text-muted-foreground mb-2">Measurements</label>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`measurement-row-${i}`}>
              <input className={inputCls} value={r.key} onChange={(e) => updateRow(i, "key", e.target.value)} placeholder="key (e.g. steps)" data-testid={`measurement-key-${i}`} />
              <input className={inputCls} value={r.label} onChange={(e) => updateRow(i, "label", e.target.value)} placeholder="label (e.g. Steps)" data-testid={`measurement-label-${i}`} />
              <input className={inputCls} value={r.unit} onChange={(e) => updateRow(i, "unit", e.target.value)} placeholder="unit (optional)" data-testid={`measurement-unit-${i}`} />
              <button type="button" onClick={() => removeRow(i)} className="p-1.5 text-muted-foreground hover:text-destructive shrink-0" data-testid={`measurement-remove-${i}`}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addRow} className="mt-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="measurement-add-row">
          <Plus className="w-3.5 h-3.5" /> Add measurement
        </button>
      </div>

      <button type="submit" disabled={busy} className="flex items-center gap-1.5 px-4 py-2.5 rounded-md font-medium text-[#2C2C2C] disabled:opacity-50" style={{ backgroundColor: HEALTH_COLOR }} data-testid="add-device-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add device
      </button>
    </form>
  );
}

export default function HealthDevices() {
  const [devices, setDevices] = useState([]);
  const [types, setTypes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, t, m] = await Promise.all([
        api.get("/health/devices"),
        api.get("/health/device-types"),
        api.get("/health/manufacturers"),
      ]);
      setDevices(d.data);
      setTypes(t.data);
      setManufacturers(m.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sync = async (id, name) => {
    setSyncingId(id);
    try {
      const { data } = await api.post(`/health/devices/${id}/sync`);
      toast.success(`${name}: synced ${data.synced} reading${data.synced === 1 ? "" : "s"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncingId(null);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/health/devices/${id}`);
      setDevices((ds) => ds.filter((d) => d.id !== id));
      toast.success("Device removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="health-devices">
      <AddDeviceForm types={types} manufacturers={manufacturers} onAdded={load} />

      {devices.length === 0 ? (
        <p className="text-sm text-muted-foreground italic" data-testid="devices-empty">No devices yet.</p>
      ) : (
        <div className="space-y-3">
          {devices.map((d) => (
            <div key={d.id} className="rounded-xl border border-border bg-card p-5 flex items-start justify-between gap-4 flex-wrap" data-testid={`device-row-${d.id}`}>
              <div className="min-w-[200px]">
                <h3 className="font-medium text-foreground">{d.name}</h3>
                <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground mt-0.5">{d.manufacturer}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {d.measurements.map((m) => m.label).join(" · ")}
                </p>
                {!d.collector_available && (
                  <p className="text-xs text-amber-700 mt-1" data-testid={`device-nocollector-${d.id}`}>No collector available for this manufacturer.</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => sync(d.id, d.name)}
                  disabled={syncingId === d.id || !d.collector_available}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium border border-input bg-white/70 hover:border-foreground/40 disabled:opacity-50 transition-colors duration-200"
                  data-testid={`device-sync-${d.id}`}
                >
                  {syncingId === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sync now
                </button>
                <button onClick={() => remove(d.id)} className="p-2 text-muted-foreground hover:text-destructive" data-testid={`device-delete-${d.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
